import React from 'react';
import { Bug, Lightbulb, Send, Link, Paperclip, X, Loader2, Clipboard, AlertTriangle, Wrench, Cog, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { base44 } from '@/api/base44Client';

const SUBMISSION_TYPES = [
  { value: 'bug', label: 'Bug Report', icon: Bug, color: 'red', track: 'tech', desc: 'Something is broken' },
  { value: 'feature', label: 'Feature Idea', icon: Lightbulb, color: 'emerald', track: 'tech', desc: 'Suggest an improvement' },
  { value: 'execution_headwind', label: 'Headwind', icon: AlertTriangle, color: 'amber', track: 'org', desc: 'Friction in execution' },
  { value: 'capability_gap', label: 'Capability Gap', icon: Wrench, color: 'sky', track: 'org', desc: 'We lack the ability' },
  { value: 'process_friction', label: 'Process Friction', icon: Cog, color: 'slate', track: 'org', desc: 'Process is getting in the way' },
  { value: 'red_line_violation', label: 'Red Line', icon: ShieldAlert, color: 'rose', track: 'org', desc: 'Values or principles at risk' },
];

const COLOR_MAP = {
  red: { bg: 'bg-red-500/15', border: 'border-red-500/40', text: 'text-red-400' },
  emerald: { bg: 'bg-emerald-500/15', border: 'border-emerald-500/40', text: 'text-emerald-400' },
  amber: { bg: 'bg-amber-500/15', border: 'border-amber-500/40', text: 'text-amber-400' },
  sky: { bg: 'bg-sky-500/15', border: 'border-sky-500/40', text: 'text-sky-400' },
  slate: { bg: 'bg-slate-500/15', border: 'border-slate-400/40', text: 'text-slate-300' },
  rose: { bg: 'bg-rose-500/15', border: 'border-rose-500/40', text: 'text-rose-400' },
};

export default function FeedbackForm({
  feedbackType, setFeedbackType,
  title, setTitle,
  link, setLink,
  description, setDescription,
  stepsToReproduce, setStepsToReproduce,
  expectedBehavior, setExpectedBehavior,
  actualBehavior, setActualBehavior,
  severity, setSeverity,
  sourceApp, setSourceApp,
  projectOptions,
  attachments, setAttachments,
  uploading, setUploading,
  isEditing,
  isPending,
  onSubmit
}) {

  const selectedType = SUBMISSION_TYPES.find(t => t.value === feedbackType);
  const isOrgType = selectedType?.track === 'org';
  const isBug = feedbackType === 'bug';
  const isFeature = feedbackType === 'feature';

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const { file_url } = await base44.integrations.Core.UploadFile({ file });
        setAttachments(prev => [...prev, { filename: file.name, url: file_url }]);
      }
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (!file) continue;
        setUploading(true);
        try {
          const { file_url } = await base44.integrations.Core.UploadFile({ file });
          setAttachments(prev => [...prev, { filename: `screenshot-${Date.now()}.png`, url: file_url }]);
        } catch (error) {
          console.error('Paste upload failed:', error);
        } finally {
          setUploading(false);
        }
        break;
      }
    }
  };

  return (
    <form onSubmit={onSubmit} className="p-4 space-y-4">
      {/* Type Picker */}
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-1.5">
          {SUBMISSION_TYPES.filter(t => t.track === 'tech').map(t => {
            const Icon = t.icon;
            const c = COLOR_MAP[t.color];
            const sel = feedbackType === t.value;
            return (
              <button key={t.value} type="button" onClick={() => setFeedbackType(t.value)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold border transition-all ${sel ? `${c.bg} ${c.border} ${c.text}` : 'bg-slate-800/40 border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-slate-300'}`}>
                <Icon className="w-3.5 h-3.5" />{t.label}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 px-1">
          <div className="h-px flex-1 bg-slate-700" />
          <span className="text-[9px] text-slate-500 font-semibold uppercase tracking-widest">or report an org issue</span>
          <div className="h-px flex-1 bg-slate-700" />
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {SUBMISSION_TYPES.filter(t => t.track === 'org').map(t => {
            const Icon = t.icon;
            const c = COLOR_MAP[t.color];
            const sel = feedbackType === t.value;
            return (
              <button key={t.value} type="button" onClick={() => setFeedbackType(t.value)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold border transition-all ${sel ? `${c.bg} ${c.border} ${c.text}` : 'bg-slate-800/40 border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-slate-300'}`}>
                <Icon className="w-3.5 h-3.5" />{t.label}
              </button>
            );
          })}
        </div>
        {selectedType && (
          <p className="text-[10px] text-slate-500 px-1">{selectedType.desc}{isOrgType ? ' — enters the High Council queue' : ' — tracked by the tech team'}</p>
        )}
      </div>

      {/* Source App */}
      {!isOrgType && (
        <div className="space-y-1.5">
          <Label className="text-slate-300 text-xs">Source App</Label>
          <Select value={sourceApp || ''} onValueChange={setSourceApp}>
            <SelectTrigger className="bg-slate-800 border-slate-700 h-8 text-xs text-slate-200" data-testid="fb-source-app-select">
              <SelectValue placeholder="Select app..." />
            </SelectTrigger>
            <SelectContent>
              {(projectOptions && projectOptions.length > 0 ? projectOptions : [
                { value: 'COMPASS', label: 'COMPASS' },
                { value: 'Schedulix', label: 'Schedulix' },
                { value: 'Ops Dashboard', label: 'Ops Dashboard' },
              ]).map(opt => (
                <SelectItem key={opt.value} value={opt.label}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[10px] text-slate-500">Auto-detected from current app. Change if reporting about a different app.</p>
        </div>
      )}

      <div className="space-y-2">
        <Label className="text-slate-300">Title</Label>
        <Input data-testid="fb-feedback-title-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={isBug ? "What's broken?" : isFeature ? "What's your idea?" : "What's the issue?"}
          className="bg-slate-800 border-slate-700 text-slate-100"
          required
        />
      </div>

      {!isOrgType && (
        <div className="space-y-2">
          <Label className="text-slate-300 flex items-center gap-2">
            <Link className="w-3 h-3" />
            Related Page/Link
          </Label>
          <Input data-testid="fb-feedback-category-input"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            placeholder="https://..."
            className="bg-slate-800 border-slate-700 text-slate-100 text-sm"
          />
          <p className="text-[10px] text-slate-500">Auto-filled with current page. Confirm or replace with another link.</p>
        </div>
      )}

      {/* Bug-specific fields */}
      {isBug && (
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">Steps to Reproduce</Label>
            <Textarea data-testid="fb-feedback-description-textarea" value={stepsToReproduce} onChange={(e) => setStepsToReproduce(e.target.value)}
              placeholder="1. Go to...&#10;2. Click on...&#10;3. See error"
              className="bg-slate-800 border-slate-700 text-slate-100 min-h-[60px] text-sm" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">Expected Behavior</Label>
            <Textarea data-testid="fb-feedback-steps-textarea" value={expectedBehavior} onChange={(e) => setExpectedBehavior(e.target.value)}
              placeholder="What should have happened?"
              className="bg-slate-800 border-slate-700 text-slate-100 min-h-[40px] text-sm" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">Actual Behavior</Label>
            <Textarea data-testid="fb-feedback-expected-textarea" value={actualBehavior} onChange={(e) => setActualBehavior(e.target.value)}
              placeholder="What actually happened?"
              className="bg-slate-800 border-slate-700 text-slate-100 min-h-[40px] text-sm" />
          </div>
        </div>
      )}

      {/* Feature description */}
      {isFeature && (
        <div className="space-y-2">
          <Label className="text-slate-300">Description</Label>
          <Textarea data-testid="fb-feedbackform-textarea-3" value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe your feature request in detail..."
            className="bg-slate-800 border-slate-700 text-slate-100 min-h-[80px]" />
        </div>
      )}

      {/* Org-type fields */}
      {isOrgType && (
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-slate-300">What's happening?</Label>
            <Textarea data-testid="fb-feedbackform-textarea-4" value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the issue — what's going wrong, what's the impact, who's affected?"
              className="bg-slate-800 border-slate-700 text-slate-100 min-h-[80px] text-sm" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">Severity</Label>
            <Select value={severity || 'medium'} onValueChange={setSeverity}>
              <SelectTrigger className="bg-slate-800 border-slate-700 h-8 text-xs text-slate-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low — minor friction</SelectItem>
                <SelectItem value="medium">Medium — noticeable impact</SelectItem>
                <SelectItem value="high">High — significant blocker</SelectItem>
                <SelectItem value="critical">Critical — urgent / values at risk</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Attachments — available for all types */}
      <div className="space-y-2">
        <Label className="text-slate-300 flex items-center gap-2">
          <Paperclip className="w-3 h-3" />
          {isBug ? 'Screenshot / Document' : 'Attachments'}
        </Label>
        <div className="flex flex-col gap-2">
          <label className="cursor-pointer">
            <input data-testid="fb-feedbackform-input-1" type="file" multiple accept="image/*,.pdf,.doc,.docx,.txt"
              onChange={handleFileUpload} className="hidden" disabled={uploading} />
            <div className="flex items-center justify-center gap-2 p-2 border border-dashed border-slate-600 rounded-md hover:border-slate-500 hover:bg-slate-800/50 transition-colors text-sm text-slate-400">
              {uploading ? (<><Loader2 className="w-4 h-4 animate-spin" />Uploading...</>) : (<><Paperclip className="w-4 h-4" />Click to attach files</>)}
            </div>
          </label>
          {attachments.length > 0 && (
            <div className="space-y-1">
              {attachments.map((file, idx) => (
                <div key={idx} className="flex items-center justify-between gap-2 p-1.5 bg-slate-800 rounded text-xs">
                  <span className="text-slate-300 truncate flex-1">{file.filename}</span>
                  <Button type="button" variant="ghost" size="icon" className="h-5 w-5 text-slate-500 hover:text-rose-400"
                    onClick={() => removeAttachment(idx)}><X className="w-3 h-3" /></Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Paste area — for bug/feature */}
      {!isOrgType && (
        <div className="space-y-2">
          <Label className="text-slate-300 flex items-center gap-2"><Clipboard className="w-3 h-3" />Paste Screenshot</Label>
          <div onPaste={handlePaste} tabIndex={0}
            className="flex items-center justify-center gap-2 p-3 border border-dashed border-slate-600 rounded-md hover:border-slate-500 hover:bg-slate-800/50 transition-colors text-xs text-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 cursor-text">
            {uploading ? (<><Loader2 className="w-4 h-4 animate-spin" />Uploading...</>) : 'Click here and paste (Ctrl+V / ⌘+V)'}
          </div>
        </div>
      )}

      <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" disabled={isPending || !title.trim()}>
        <Send className="w-4 h-4 mr-2" />
        {isPending ? 'Submitting...' : (isEditing ? 'Update' : isOrgType ? 'Report Issue' : 'Submit Feedback')}
      </Button>
    </form>
  );
}